import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import FigmaDesignsSection from '../Figma/FigmaDesignsSection';
import {
  buildScreenshotSectionModel,
  PKB_SCREENSHOT_COPY,
  truncateExtractedText,
} from './pkbScreenshotPresentation';
import {
  formatDocumentText,
  formatSectionTitle,
  getPdfModalExtractionLabel,
} from './productKnowledgeUtils';
import { SV } from '../../automation/stratovaiTestIds';

const ProductKnowledgeDocumentModal = ({
  open,
  onClose,
  docContent,
  imageBlobUrls,
  projectId,
  onFramesUpdated,
}) => {
  const extractionLabel = getPdfModalExtractionLabel(docContent.extraction);
  const screenshotSection = buildScreenshotSectionModel(docContent.images, imageBlobUrls, docContent);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ 'data-testid': SV.pk.documentModal }}>
      <DialogTitle>{formatDocumentText(docContent.title) || 'Document'}</DialogTitle>
      <DialogContent dividers data-testid={SV.pk.documentModalContent}>
        {docContent.meta && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {docContent.meta.priority && <Chip size="small" label={`Priority: ${docContent.meta.priority}`} color="warning" variant="outlined" />}
            {docContent.meta.issueType && <Chip size="small" label={`Type: ${docContent.meta.issueType}`} variant="outlined" />}
            {docContent.meta.status && <Chip size="small" label={`Status: ${docContent.meta.status}`} color="info" variant="outlined" />}
            {docContent.meta.assignee && <Chip size="small" label={`Assignee: ${docContent.meta.assignee}`} variant="outlined" />}
            {Array.isArray(docContent.meta.labels) &&
              docContent.meta.labels.slice(0, 5).map((label) => (
                <Chip key={label} size="small" label={label} variant="outlined" />
              ))}
          </Box>
        )}

        {extractionLabel && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip
              size="small"
              label={extractionLabel}
              color={docContent.extraction?.ocr_used ? 'warning' : 'success'}
              variant="outlined"
            />
            {Number.isFinite(docContent.extraction?.page_count) && (
              <Chip
                size="small"
                label={`${docContent.extraction.page_count} page${docContent.extraction.page_count === 1 ? '' : 's'}`}
                variant="outlined"
              />
            )}
          </Box>
        )}

        {(docContent.jiraKey || docContent.jiraUrl || docContent.meta?.subtasksCount) && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              JIRA
            </Typography>
            {docContent.jiraKey && <Chip size="small" label={docContent.jiraKey} variant="outlined" />}
            {Number.isFinite(docContent.meta?.subtasksCount) && docContent.meta.subtasksCount > 0 && (
              <Chip size="small" label={`Subtasks: ${docContent.meta.subtasksCount}`} variant="outlined" />
            )}
            {docContent.jiraUrl && (
              <Button
                size="small"
                variant="text"
                onClick={() => window.open(docContent.jiraUrl, '_blank')}
                sx={{ textTransform: 'none', minWidth: 'auto', px: 0 }}
              >
                Open in JIRA
              </Button>
            )}
          </Box>
        )}

        {docContent.description && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Description
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {formatDocumentText(docContent.description)}
            </Typography>
          </Box>
        )}

        {docContent.sections && Object.keys(docContent.sections).length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {Object.entries(docContent.sections).map(([key, val]) => {
              const isInferred = docContent.inferredSections?.[key] === true;
              return (
                <Box key={key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {formatSectionTitle(key)}
                    </Typography>
                    {isInferred && (
                      <Chip
                        label="Inferred"
                        size="small"
                        color="info"
                        variant="outlined"
                        icon={<span style={{ fontSize: '14px' }}>🤖</span>}
                        sx={{
                          fontSize: '0.7rem',
                          height: '22px',
                          fontStyle: 'italic',
                        }}
                      />
                    )}
                  </Box>
                  {Array.isArray(val) ? (
                    <Box component="ul" sx={{ pl: 3, mt: 0 }}>
                      {val.map((entry, index) => (
                        <li key={index}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                            {formatDocumentText(entry)}
                          </Typography>
                        </li>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {formatDocumentText(val)}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        ) : null}

        {screenshotSection.visible ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {screenshotSection.title}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 1 }}>
              {screenshotSection.items.map((item) => (
                <Box key={item.key} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  {item.mode === 'image' ? (
                    <>
                      <img
                        src={item.blobUrl}
                        alt={item.alt}
                        style={{ width: '100%', height: 'auto', borderRadius: 4 }}
                      />
                      {item.extractedText ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {truncateExtractedText(item.extractedText)}
                        </Typography>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                        {PKB_SCREENSHOT_COPY.extractedDesignTextCardLabel}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mt: 0.5 }}>
                        {truncateExtractedText(item.text)}
                      </Typography>
                    </>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}

        {docContent.entryId && (docContent.hasFigma || (docContent.figmaFrames || []).length > 0) && (
          <FigmaDesignsSection
            pkbEntryId={docContent.entryId}
            projectId={projectId}
            initialFrames={docContent.figmaFrames}
            hasFigma={docContent.hasFigma}
            onFramesUpdated={onFramesUpdated}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button data-testid={SV.pk.documentModalClose} onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductKnowledgeDocumentModal;
